// Lister-Detail-Tab "Varianten" — Tabelle aller Varianten (Vater + Kinder) eines
// Variantenvaters mit Status-Balken "X/Y Pflicht + Variantenattribute" (Luecke rot).
//
// ACHTUNG, nicht mit dem Mapping-Reiter verwechseln: Dessen "X / Y Felder gemappt" zaehlt
// die Felder des PROFILS. Hier zaehlt der Balken seit dem 2026-08-16 nur die Pflichtfelder
// plus die am Vater gewaehlten Variantenattribute — also das, was DIESE Variante braucht.
// Vorher stand hier dieselbe Beschriftung mit einer aehnlichen Zahl, und der Betreiber las
// sie zu Recht als "der aktuelle Mapping-Zuordnungs-Fortschrittsbalken".
//
// Backend: kaufland-listing-preview action=variant-check (aggregiert pro Artikel).
//
// Die Zeile fuehrt nur noch EINE Aktion: "Mapping" oeffnet die Verkaufskanal-Zuordnung
// der Variante in Vario in einem neuen Fenster. Bis zum 2026-08-16 lagen hier drei
// Knoepfe (Mapping/Bilder/Vorschau), die renderMappingTab/renderImagesTab/
// renderPreviewTab in einem Overlay nachbauten. Das ist auf Wunsch des Betreibers
// entfallen: Gepflegt wird die Zuordnung dort, wo sie hingehoert — in Vario. Die
// Bilder liegen seither im eigenen Reiter "Bilder" (Bild-Matrix ueber die ganze
// Familie), die Push-Vorschau am Einzel-Listing.

import type { SupabaseClient } from '@supabase/supabase-js';
import { showToast } from '@vario-connect/frontend-design';
import { ladeVarioBasisUrl } from '../../lib/vario-base-url.js';
import { localeForStorefront as localeFor } from '../../lib/storefront-locale.js';
import { zeilenFuerVerkaufsbedingungen } from '../../lib/variant-offer-overrides.js';
import { variantenStatusZelle, alleImEndzustand, timerMerken, timerVergessen } from '../../lib/variant-status.js';
// Dieselbe Umrechnung wie im Detail-Fenster, inklusive "kein Preis <= 0". Ein zweiter
// Nachbau waere eine zweite Wahrheit ueber dieselbe Regel.
import { resolveOfferOverridesFromUnit } from '../shared/offer-conditions-form.js';
import {
  selectableVariantAttributes,
  toggleVariantAttribute,
  mapToNames,
  MAX_VARIANT_ATTRIBUTES,
  balkenFarbe,
  fehlendeKinder,
  onlineStand,
  variantAttrWarnung,
  type Balkenstand,
  type OnlineStand,
  type SelectableAttribute,
  type VariantAttributeMapEntry,
} from '../../lib/variant-attribute-selection.js';

/** Eine Vario-Variantendimension, geliefert vom `variant-check`. */
interface VarioVariantAttribute {
  id: string;
  label: string;
}

export interface VariantsTabCtx {
  supabase: SupabaseClient;
  supabaseUrl: string;
  session: { token: string };
}
export interface VariantsTabListing {
  /** Listing-UUID des Variantenvaters — Anker fuer `kaufland_set_variant_attributes`. */
  id: string;
  storefront: string;
  kaufland_category_id: number | null;
  vario_category_id: string | null;
  vario_listing_id: string;
  vario_article_id: string;
}

interface VariantCheckRow {
  articleId: string;
  listingId: string | null;
  isParent: boolean;
  combinationLabel: string;
  filledCount: number;
  totalCount: number;
  requiredGap: boolean;
  requiredMissing: string[];
  hasImage: boolean;
  /** Am Vater gewaehlte Variantenattribute ohne Wert in diesem Kind.
   *  Optional, damit ein noch nicht deployter `variant-check` die Tabelle nicht bricht. */
  variantAttrMissing?: string[];
  /** Drei getrennte Fortschritte. Fehlt das Feld, faellt die Zelle auf den alten Einzelbalken. */
  balken?: { pflicht: Balkenstand; kategorie: Balkenstand; varianten: Balkenstand };
  /** Wie die Zeile zustande kam. `fehler` darf NICHT wie ein Befund aussehen. */
  pruefstatus?: 'ok' | 'kein-profil' | 'kein-listing' | 'fehler';
  fehlerText?: string;
  /** Laeuft bei Kaufland ein aktives Angebot? Aus dem Spiegel, nicht aus unserem Status.
   *  `undefined` heisst "unbekannt" und ist NICHT dasselbe wie `false`. */
  online?: boolean;
  /** Die aus dem Mapping aufgeloesten Angebotsfelder (Preis, Menge, Zustand, Lieferzeit,
   *  Versandgruppe, MwSt). Fehlt, solange die alte Preview-Function ausgeliefert ist —
   *  dann werden beim Anlegen keine Verkaufsbedingungen geschrieben. */
  unitPayload?: Record<string, unknown>;
  /** Ob im Snapshot schon brauchbare Verkaufsbedingungen stehen. Fehlt = unbekannt, dann
   *  wird NICHT geschrieben (siehe zeilenFuerVerkaufsbedingungen). */
  hatVerkaufsbedingungen?: boolean;
  /** Interner Zustand aus kaufland.listings. Heisst nicht `status`, weil `pruefstatus`
   *  daneben etwas anderes bedeutet (Feldpruefung vs. Aufschalt-Ablauf). */
  listingStatus?: string;
  lastError?: string | null;
  statusReason?: string | null;
  unit?: { status?: string | null; stock?: number | null };
}

function escapeHtml(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&#39;');
}

// Sentinel-Tokens aus dem Backend in lesbaren Text uebersetzen — nie roh anzeigen.
function prettyMissing(keys: string[]): string {
  return keys
    .map((k) => {
      if (k === '__error__') return 'Fehler beim Laden';
      if (k === '__article_not_loaded__') return 'Artikel nicht ladbar';
      return k;
    })
    .join(', ');
}

async function callFn<T>(ctx: VariantsTabCtx, name: string, body: unknown): Promise<{ ok: true; value: T } | { ok: false; error: string }> {
  try {
    const res = await fetch(`${ctx.supabaseUrl.replace(/\/$/, '')}/functions/v1/${name}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${ctx.session.token}` },
      body: JSON.stringify(body),
    });
    const text = await res.text();
    let parsed: unknown;
    try { parsed = text ? JSON.parse(text) : {}; } catch { parsed = { raw: text }; }
    if (!res.ok) {
      const eb = (parsed as { error?: { code?: string; message?: string } } | null)?.error;
      return { ok: false, error: eb ? `${eb.code ?? '?'}: ${eb.message ?? '?'}` : `HTTP ${res.status}` };
    }
    return { ok: true, value: parsed as T };
  } catch (e) {
    return { ok: false, error: (e as Error).message };
  }
}

/**
 * Schreibt frisch angelegten Kindern die Verkaufsbedingungen aus dem Mapping.
 *
 * Anlass (2026-08-17): "Alle fehlenden anlegen" legte die Kind-Listings an, schrieb aber
 * keine `offerOverrides` — der Snapshot enthielt nur `ean`, `description` und
 * `kauflandCategoryId`. Alle acht Kinder der Bierdeckel-Familie standen deshalb ohne, und
 * jeder Push scheiterte an `listing_price (cents, > 0) missing in offerOverrides`. Das eine
 * von Hand gepflegte Listing war das einzige, das bis Kaufland kam. Der Betreiber: "Ich
 * glaube das bei alle Anlegen, vergessen wurde den Kanal Preis zu setzen."
 *
 * Die Werte kommen aus derselben Kette wie im Detail-Fenster: aufgeloester `unitPayload`
 * plus `resolveOfferOverridesFromUnit`, inklusive dessen Regel "kein Preis <= 0". Fehlt ein
 * Pflichtwert, wird fuer diese Zeile NICHTS geschrieben — ein halbes Objekt saehe im
 * Snapshot wie eine gepflegte Angabe aus, und der Haendler bekaeme spaeter einen Preis von
 * Kaufland zurueckgewiesen statt hier eine Luecke zu sehen.
 *
 * Fail-soft in beide Richtungen: Ein Fehler beim Speichern bricht das Rendern nicht ab. Die
 * Listings existieren dann trotzdem, nur ohne Bedingungen — genau der Zustand von vorher,
 * und im Detail-Fenster nachholbar.
 */
async function schreibeVerkaufsbedingungen(
  ctx: VariantsTabCtx,
  rows: readonly VariantCheckRow[],
): Promise<void> {
  const kandidaten = zeilenFuerVerkaufsbedingungen(rows);
  if (kandidaten.length === 0) return;

  let gesetzt = 0;
  let ohneWerte = 0;
  for (const zeile of kandidaten) {
    const aufgeloest = resolveOfferOverridesFromUnit(zeile.unitPayload ?? {}, null);
    if (!aufgeloest.ok) { ohneWerte += 1; continue; }
    const { error } = await ctx.supabase.rpc('kaufland_save_attach_overrides', {
      _listing_id: zeile.listingId,
      _overrides: aufgeloest.overrides,
    });
    if (error) {
      console.warn('[variants] Verkaufsbedingungen nicht gespeichert', zeile.articleId, error.message);
      ohneWerte += 1;
      continue;
    }
    gesetzt += 1;
  }

  if (gesetzt > 0) {
    showToast(
      ohneWerte > 0
        ? `Verkaufsbedingungen für ${gesetzt} Variante(n) übernommen, ${ohneWerte} ohne (fehlender Kanalpreis oder Pflichtwert).`
        : `Verkaufsbedingungen für ${gesetzt} Variante(n) übernommen.`,
      ohneWerte > 0 ? 'info' : 'success',
    );
  } else if (ohneWerte > 0) {
    showToast(
      `Für ${ohneWerte} Variante(n) fehlen Verkaufsbedingungen (meist der Kanal-Verkaufspreis in Vario).`,
      'info',
    );
  }
}

export async function renderVariantsTab(
  host: HTMLElement,
  ctx: VariantsTabCtx,
  listing: VariantsTabListing,
  /** Vario-Artikel-IDs, die dieser Durchlauf GERADE angelegt hat. Fuer sie werden die
   *  Verkaufsbedingungen aus dem Mapping geschrieben, bevor die Tabelle erscheint —
   *  siehe schreibeVerkaufsbedingungen(). Leer bei jedem normalen Rendern. */
  neuAngelegt: readonly string[] = [],
): Promise<void> {
  host.innerHTML = `<div class="klv-loading"><span class="v-icon fa-regular fa-spinner-third fa-spin"></span> Lade Varianten …</div>`;

  if (!listing.kaufland_category_id) {
    host.innerHTML = `<div class="klv-empty">Dieses Listing hat noch keine Kaufland-Kategorie. Bitte zuerst im Vario-Slot zuweisen.</div>`;
    return;
  }

  const { data: profileResp, error: profileErr } = await ctx.supabase.rpc('kaufland_get_mapping_profile', {
    _storefront: listing.storefront, _locale: localeFor(listing.storefront), _category_id: listing.kaufland_category_id,
  });
  if (profileErr) { host.innerHTML = `<div class="klv-empty klv-empty--error">Profil-Ladung fehlgeschlagen: ${escapeHtml(profileErr.message)}</div>`; return; }
  const profile = (profileResp as { profile?: { id: string } | null } | null)?.profile ?? null;

  // OHNE Profil wird NICHT mehr abgebrochen (Aenderung 2026-08-15).
  //
  // Frueher endete der Reiter hier mit "Noch kein Mapping-Profil gespeichert". Das sperrte
  // aber nicht nur die Feldpruefung, fuer die das Profil noetig ist, sondern auch das Anlegen
  // fehlender Kind-Listings -- eine reine Vario-Operation, die mit Kaufland-Feldern nichts zu
  // tun hat. Der Haendler landete damit in einer Reihenfolge-Falle: Er sollte mappen, bevor
  // es die Artikel gab, die zu mappen waeren.
  // Client-seitige Gesamtdauer: Sie schliesst Netz und Warteschlange mit ein, die
  // Server-Messung nicht. Weichen beide stark ab, liegt es nicht an der Function.
  const tStart = Date.now();
  const resp = await callFn<{
    rows: VariantCheckRow[];
    varioVariantAttributes?: VarioVariantAttribute[];
    spiegelStand?: string | null;
    meta?: { dauer?: Record<string, number>; zeilen?: number };
  }>(
    ctx,
    'kaufland-listing-preview',
    profile
      ? {
          action: 'variant-check',
          profileId: profile.id,
          articleId: listing.vario_article_id,
          listingId: listing.vario_listing_id,
        }
      : {
          // Ohne Profil kennt die Function die Storefront nicht -- sie kommt sonst von dort.
          action: 'variant-check',
          storefront: listing.storefront,
          articleId: listing.vario_article_id,
          listingId: listing.vario_listing_id,
        },
  );
  if (!resp.ok) { host.innerHTML = `<div class="klv-empty klv-empty--error">Varianten-Check fehlgeschlagen: ${escapeHtml(resp.error)}</div>`; return; }

  // Frisch angelegte Kinder bekommen ihre Verkaufsbedingungen, BEVOR die Tabelle erscheint.
  // Hier und nicht in enqueueCreate, weil erst dieser Aufruf den aufgeloesten unitPayload
  // liefert; ein zweiter Preview-Durchlauf waere derselbe teure Vario-Rundlauf noch einmal.
  if (neuAngelegt.length > 0) {
    await schreibeVerkaufsbedingungen(ctx, resp.value.rows);
  }

  // Vario-Base-URL fail-soft laden — wenn nicht verfuegbar bleibt der Link einfach weg.
  const varioBaseUrl = await ladeVarioBasisUrl(ctx.supabase);

  // Variantenattribute: auswaehlbare Namen aus dem Kategorie-Schema, aktuelle Auswahl vom
  // Vater. Beides fail-soft — faellt eines aus, zeigt die Vaterzeile nur einen Hinweis statt
  // die ganze Tabelle zu verlieren. Das Schema braucht KEIN Mapping-Profil, es beschreibt die
  // Kategorie, nicht die Zuordnung.
  let auswaehlbar: SelectableAttribute[] = [];
  let gewaehlt: string[] = [];
  const schema = await callFn<{ fields: Array<{ key: string; label: string; fieldGroup?: string }> }>(
    ctx,
    'kaufland-listing-schema',
    { storefront: listing.storefront, locale: localeFor(listing.storefront), categoryId: listing.kaufland_category_id },
  );
  if (schema.ok) auswaehlbar = selectableVariantAttributes(schema.value.fields ?? []);

  // Vario-Dimensionen ("Farbe", "Groesse") aus derselben Antwort wie die Varianten. Sind sie
  // da, wird zugeordnet statt angekreuzt.
  const varioDimensionen: VarioVariantAttribute[] = resp.value.varioVariantAttributes ?? [];

  const { data: vaZeile } = await ctx.supabase
    .schema('kaufland')
    .from('listings')
    .select('variant_attribute_names, variant_attribute_map')
    .eq('id', listing.id)
    .maybeSingle();
  const zeile = vaZeile as {
    variant_attribute_names?: string[] | null;
    variant_attribute_map?: VariantAttributeMapEntry[] | null;
  } | null;
  if (Array.isArray(zeile?.variant_attribute_names)) gewaehlt = zeile.variant_attribute_names;
  const zuordnung: VariantAttributeMapEntry[] = Array.isArray(zeile?.variant_attribute_map)
    ? zeile.variant_attribute_map
    : [];

  renderTable(host, ctx, listing, resp.value.rows, varioBaseUrl, profile === null, auswaehlbar, gewaehlt, varioDimensionen, zuordnung, resp.value.spiegelStand ?? null);

  // Zeitmessung anzeigen (2026-08-18, Diagnose der langen Ladezeit). Bewusst als
  // unauffaellige Zeile am Fuss statt als Konsolen-Ausgabe: Der Betreiber soll sie sehen und
  // vorlesen koennen, ohne die Entwicklerwerkzeuge zu oeffnen.
  zeigeZeitmessung(host, resp.value.meta?.dauer, resp.value.meta?.zeilen, Date.now() - tStart);
}

/**
 * Verdrahtet die Attributauswahl der Vaterzeile.
 *
 * Gespeichert wird erst auf Knopfdruck, nicht bei jedem Haken: Die RPC prueft serverseitig
 * gegen die Kategorie und die Fuenfergrenze, und ein Speichern je Klick erzeugte fuenf
 * Aufrufe fuer eine Auswahl. Der Knopf ist deaktiviert, solange sich nichts geaendert hat.
 */
function wireVariantAttrs(
  host: HTMLElement,
  ctx: VariantsTabCtx,
  listing: VariantsTabListing,
  auswaehlbar: SelectableAttribute[],
  ausgangslage: string[],
  varioDimensionen: VarioVariantAttribute[] = [],
): void {
  const wrap = host.querySelector<HTMLElement>('.klv-vattr-wrap');
  if (!wrap) return;
  const sumEl = wrap.querySelector<HTMLElement>('[data-vattr-sum]');
  const saveBtn = wrap.querySelector<HTMLButtonElement>('.klv-vattr-save');
  const beschriftung = new Map(auswaehlbar.map((a) => [a.key, a.label]));

  // Zuordnungs-Modus: Die Dropdowns sind die Wahrheit, die Namensliste leitet sich daraus ab.
  const selects = [...wrap.querySelectorAll<HTMLSelectElement>('.klv-vattr-select')];
  if (selects.length > 0) {
    const ausMaske = (): VariantAttributeMapEntry[] => selects.map((s) => ({
      vario_id: s.dataset['varioId'] ?? '',
      vario_label: s.dataset['varioLabel'] ?? '',
      kaufland_name: s.value,
    }));
    const start = JSON.stringify(mapToNames(ausMaske()));

    const zeichnenMap = (): void => {
      const namen = mapToNames(ausMaske());
      if (sumEl) {
        sumEl.textContent = namen.length
          ? namen.map((k) => beschriftung.get(k) ?? k).join(', ')
          : 'keine, der Push leitet sie dann aus den Varianten ab';
      }
      if (saveBtn) saveBtn.disabled = JSON.stringify(namen) === start;
    };

    selects.forEach((s) => s.addEventListener('change', () => {
      const namen = mapToNames(ausMaske());
      if (namen.length > MAX_VARIANT_ATTRIBUTES) {
        showToast(`Höchstens ${MAX_VARIANT_ATTRIBUTES} Variantenattribute — bitte zuerst eines abwählen.`, 'error');
        s.value = '';
      }
      zeichnenMap();
    }));

    saveBtn?.addEventListener('click', () => {
      void (async () => {
        saveBtn.disabled = true;
        const map = ausMaske();
        const { error } = await ctx.supabase.rpc('kaufland_set_variant_attributes', {
          _listing_id: listing.id,
          _names: null,
          _map: map.some((e) => e.kaufland_name !== '') ? map : null,
        });
        if (error) { showToast(error.message, 'error'); saveBtn.disabled = false; return; }
        showToast('Variantenattribute gespeichert', 'success');
        saveBtn.disabled = true;
      })();
    });

    zeichnenMap();
    return;
  }

  let auswahl = [...ausgangslage].sort((a, b) => a.localeCompare(b));

  const gleich = (a: string[], b: string[]): boolean =>
    a.length === b.length && a.every((x, i) => x === b[i]);

  const zeichnen = (): void => {
    if (sumEl) {
      sumEl.textContent = auswahl.length
        ? auswahl.map((k) => beschriftung.get(k) ?? k).join(', ')
        : 'keine, der Push leitet sie dann aus den Varianten ab';
    }
    if (saveBtn) saveBtn.disabled = gleich(auswahl, [...ausgangslage].sort((a, b) => a.localeCompare(b)));
  };

  wrap.querySelectorAll<HTMLInputElement>('.klv-vattr-box').forEach((box) => {
    box.addEventListener('change', () => {
      const res = toggleVariantAttribute(auswahl, box.value);
      if (res.rejected) {
        // Abgelehnt: Haken zuruecknehmen, damit Anzeige und Zustand nicht auseinanderlaufen.
        box.checked = false;
        showToast(res.rejected, 'error');
        return;
      }
      auswahl = res.selection;
      zeichnen();
    });
  });

  saveBtn?.addEventListener('click', () => {
    void (async () => {
      saveBtn.disabled = true;
      const { data, error } = await ctx.supabase.rpc('kaufland_set_variant_attributes', {
        _listing_id: listing.id,
        _names: auswahl.length > 0 ? auswahl : null,
      });
      if (error) {
        // Die Meldung der RPC durchreichen statt sie zu glaetten: "keine Attribute der
        // Kategorie 40941: farbe" sagt dem Haendler, WAS er falsch gewaehlt hat.
        showToast(error.message, 'error');
        saveBtn.disabled = false;
        return;
      }
      ausgangslage = Array.isArray(data) ? (data as string[]) : [];
      auswahl = [...ausgangslage].sort((a, b) => a.localeCompare(b));
      zeichnen();
      showToast('Variantenattribute gespeichert', 'success');
    })();
  });

  zeichnen();
}

/**
 * Der Online-Stand neben der Varianten-Ueberschrift.
 *
 * Der Hinweis auf das Alter des Spiegels ist kein Schmuck: `kaufland.units` wird alle zwei
 * Stunden aufgefrischt. Direkt nach dem Listen steht dort noch nichts, und ohne diesen
 * Hinweis liest sich "0 von 8 online" wie ein Fehlschlag statt wie "noch nicht abgeholt".
 */
function onlineBadge(stand: OnlineStand, spiegelStand: string | null): string {
  if (stand.gesamt === 0) return '';
  const alter = spiegelStand ? alterText(spiegelStand) : null;
  const titel = alter
    ? `Stand des Kaufland-Spiegels: ${alter}. Er wird alle zwei Stunden aufgefrischt, direkt nach dem Listen ist ein Angebot hier noch nicht sichtbar.`
    : 'Aus dem Kaufland-Spiegel, der alle zwei Stunden aufgefrischt wird. Direkt nach dem Listen ist ein Angebot hier noch nicht sichtbar.';
  const cls = stand.alleOnline ? 'klv-online klv-online--ok' : 'klv-online';
  const zahl = stand.unbekannt > 0
    ? `${stand.online} / ${stand.gesamt} online, ${stand.unbekannt} unbekannt`
    : `${stand.online} / ${stand.gesamt} online`;
  return `<span class="${cls}" title="${escapeHtml(titel)}">${escapeHtml(zahl)}</span>`;
}

/** "vor 12 Minuten" / "vor 3 Stunden" — grob genug, um das Alter einzuschaetzen. */
function alterText(iso: string): string | null {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  const min = Math.max(0, Math.round((Date.now() - d.getTime()) / 60000));
  if (min < 1) return 'gerade eben';
  if (min < 60) return `vor ${min} Minute${min === 1 ? '' : 'n'}`;
  const std = Math.round(min / 60);
  return `vor ${std} Stunde${std === 1 ? '' : 'n'}`;
}

/**
 * Die drei Fortschrittsbalken einer Varianten-Zeile.
 *
 * Ein einzelner Balken konnte die drei Fragen nicht gleichzeitig beantworten, und der
 * Versuch hat genau die Verwirrung erzeugt, die der Betreiber gemeldet hat. Jetzt:
 *   1. Pflicht — nimmt Kaufland das Angebot ueberhaupt an?
 *   2. Kategorieattribute — wie vollstaendig sind die Angaben dieser Kategorie?
 *   3. Variantenattribute — kann Kaufland die Gruppe bilden? (nur wenn welche gewaehlt sind)
 *
 * Gruen sobald vollstaendig, nicht erst wenn zusaetzlich kein Pflichtfeld fehlt.
 *
 * Faellt `balken` aus (alte Function noch nicht deployt), bleibt der alte Einzelbalken
 * stehen statt einer leeren Zelle.
 */
function dreiBalken(r: VariantCheckRow): string {
  if (!r.balken) {
    const pct = r.totalCount > 0 ? Math.round((r.filledCount / r.totalCount) * 100) : 0;
    return `<div class="klv-bar"><div class="klv-bar-fill ${r.requiredGap ? 'klv-bar--gap' : 'klv-bar--ok'}" style="width:${pct}%"></div></div>
      <span class="klv-count">${r.filledCount}/${r.totalCount}</span>`;
  }
  const zeile = (titel: string, stand: { filled: number; total: number }, hinweis: string): string => {
    const farbe = balkenFarbe(stand);
    const pct = stand.total > 0 ? Math.round((stand.filled / stand.total) * 100) : 0;
    const cls = farbe === 'voll' ? 'klv-bar--ok' : farbe === 'luecke' ? 'klv-bar--gap' : 'klv-bar--leer';
    return `
      <div class="klv-bar-zeile" title="${escapeHtml(hinweis)}">
        <span class="klv-bar-titel">${escapeHtml(titel)}</span>
        <div class="klv-bar"><div class="klv-bar-fill ${cls}" style="width:${pct}%"></div></div>
        <span class="klv-count ${farbe === 'luecke' ? 'klv-count--gap' : ''}">${stand.filled}/${stand.total}</span>
      </div>`;
  };
  const b = r.balken;
  return `<div class="klv-balken">
    ${zeile('Pflicht', b.pflicht, 'Die Felder, ohne die Kaufland das Angebot gar nicht annimmt.')}
    ${zeile('Kategorie', b.kategorie, 'Die Attribute dieser Kaufland-Kategorie, pflichtig und optional.')}
    ${b.varianten.total > 0 ? zeile('Varianten', b.varianten, 'Die am Vater gewählten variantenbildenden Attribute. Fehlt eines, kann Kaufland diese Variante nicht in die Gruppe einsortieren.') : ''}
  </div>`;
}

/** Baut die Vario-UI-URL fuer eine Varianten-Zeile.
 *  Mit Listing-ID → Listing-Detail (= Verkaufskanal-Zuordnung der Variante).
 *  Ohne Listing-ID → Article-Detail (allgemein). */
function varioUrlForRow(baseUrl: string, articleId: string, listingId: string | null): string {
  const aid = encodeURIComponent(articleId);
  if (listingId) {
    return `${baseUrl}/products/articles/${aid}/listings/${encodeURIComponent(listingId)}`;
  }
  return `${baseUrl}/products/articles/${aid}/detail`;
}

/**
 * Die Mehrfachauswahl der Variantenattribute in der Vaterzeile.
 *
 * Kaestchenliste statt `<select multiple>`: Bei 37 Kategorie-Attributen ist ein natives
 * Mehrfach-Select mit Strg-Klick unbedienbar, und ein versehentlicher Klick verwirft dort die
 * ganze bisherige Auswahl. Die Liste scrollt in sich, die Auswahl steht als Zusammenfassung
 * darueber — so sieht man sie auch bei gescrollter Liste.
 */
function variantAttrPicker(
  auswaehlbar: SelectableAttribute[],
  gewaehlt: string[],
  varioDimensionen: VarioVariantAttribute[] = [],
  zuordnung: VariantAttributeMapEntry[] = [],
): string {
  if (auswaehlbar.length === 0) {
    return `<span class="v-text-muted">Variantenattribute konnten nicht geladen werden.</span>`;
  }

  // Liefert Vario Dimensionen, wird zugeordnet statt angekreuzt: links die Vario-Dimension,
  // rechts das Kaufland-Attribut. Liefert Vario keine — etwa weil das Variantenschema ohne
  // Bezeichnungen gepflegt ist —, bleibt die Ankreuzliste. Sonst staende die Vaterzeile bei
  // leerem Schema ohne jede Bedienmoeglichkeit da.
  if (varioDimensionen.length > 0) {
    const gewaehltFuer = new Map(zuordnung.map((z) => [z.vario_id, z.kaufland_name]));
    const zeilen = varioDimensionen.map((d) => {
      const aktuell = gewaehltFuer.get(d.id) ?? '';
      const optionen = auswaehlbar.map((a) =>
        `<option value="${escapeHtml(a.key)}"${a.key === aktuell ? ' selected' : ''}>${escapeHtml(a.label)} (${escapeHtml(a.key)})</option>`).join('');
      return `
        <div class="klv-vattr-row">
          <span class="klv-vattr-vario">${escapeHtml(d.label)}</span>
          <span class="klv-vattr-pfeil">→</span>
          <select class="kl-input klv-vattr-select" data-vario-id="${escapeHtml(d.id)}" data-vario-label="${escapeHtml(d.label)}">
            <option value=""${aktuell === '' ? ' selected' : ''}>— nicht zuordnen —</option>
            ${optionen}
          </select>
        </div>`;
    }).join('');

    return `
      <div class="klv-vattr-wrap">
        <div class="klv-vattr-head">
          <strong>Variantenattribute</strong>
          <span class="klv-vattr-sum" data-vattr-sum></span>
          <button type="button" class="v-button v-button--sm klv-vattr-save" disabled>Speichern</button>
        </div>
        <div class="klv-vattr-map">${zeilen}</div>
      </div>`;
  }
  const beschriftung = new Map(auswaehlbar.map((a) => [a.key, a.label]));
  const zusammenfassung = gewaehlt.length
    ? gewaehlt.map((k) => escapeHtml(beschriftung.get(k) ?? k)).join(', ')
    : 'keine, der Push leitet sie dann aus den Varianten ab';

  const kaestchen = auswaehlbar.map((a) => `
    <label class="klv-vattr-item">
      <input type="checkbox" class="klv-vattr-box" value="${escapeHtml(a.key)}"${gewaehlt.includes(a.key) ? ' checked' : ''}>
      <span>${escapeHtml(a.label)}</span>
      <code class="klv-vattr-key">${escapeHtml(a.key)}</code>
    </label>`).join('');

  return `
    <div class="klv-vattr-wrap">
      <div class="klv-vattr-head">
        <strong>Variantenattribute</strong>
        <span class="klv-vattr-sum" data-vattr-sum>${zusammenfassung}</span>
        <button type="button" class="v-button v-button--sm klv-vattr-save" disabled>Speichern</button>
      </div>
      <div class="klv-vattr-list">${kaestchen}</div>
    </div>`;
}

function renderTable(host: HTMLElement, ctx: VariantsTabCtx, listing: VariantsTabListing, rows: VariantCheckRow[], varioBaseUrl: string | null, ohneProfil = false, auswaehlbar: SelectableAttribute[] = [], gewaehlt: string[] = [], varioDimensionen: VarioVariantAttribute[] = [], zuordnung: VariantAttributeMapEntry[] = [], spiegelStand: string | null = null): void {
  // "X / Y listbar" zaehlt NUR die Varianten — der Vater ist kein Kaufland-Produkt
  // (jede Variante ist eins, mit eigener EAN) und wird nie gepusht. Frueher lief er
  // mit in den Zaehler und verfaelschte ihn (bzw. haing wegen fehlender Vater-EAN
  // dauerhaft als "nicht listbar" drin).
  const children = rows.filter((r) => !r.isParent);
  // Eine gescheiterte Zeile ist NICHT listbar. Seit `requiredGap` dort auf false steht
  // (damit sie nicht rot wie eine Pflichtluecke aussieht), waere sie sonst mitgezaehlt
  // worden — geprueft wurde sie ja gerade nicht.
  const listable = children.filter((r) => !r.requiredGap && r.listingId && r.pruefstatus !== 'fehler').length;
  // Gescheiterte Zeilen zaehlen NICHT als fehlend — siehe fehlendeKinder().
  const missingChildren = fehlendeKinder(rows);
  const stand = onlineStand(rows);
  // Listbar sind Kinder MIT Vario-Listing. Ohne Listing gibt es nichts aufzuschalten —
  // die legt der Block "Alle fehlenden anlegen" darueber an.
  const listbareKinder = rows.filter((r) => !r.isParent && r.listingId);
  const createBlock = missingChildren.length > 0 ? `
    <div class="klv-create">
      <span class="klv-create-label">Varianten anlegen:</span>
      <label><input type="checkbox" class="klv-inh" data-inh="description" checked> Beschreibung</label>
      <label><input type="checkbox" class="klv-inh" data-inh="price" checked> Preis</label>
      <label><input type="checkbox" class="klv-inh" data-inh="optional_fields" checked> optionale Felder</label>
      <label><input type="checkbox" class="klv-inh" data-inh="name" checked> Name</label>
      <button type="button" class="v-button v-button--primary v-button--sm klv-create-all">Alle fehlenden anlegen (${missingChildren.length})</button>
    </div>` : '';

  // Zeilen, denen die Verkaufsbedingungen fehlen. EIGENER Block, absichtlich unabhaengig von
  // `createBlock`: Der verschwindet, sobald alle Kinder existieren — und genau dann standen
  // die Altfaelle ohne Auslauf da (gemessen 2026-08-17: fuenf Zeilen auf `offerOverrides:
  // null` und `status = error`, waehrend der Anlegen-Knopf laengst weg war).
  //
  // Ein Knopf und kein stiller Nebeneffekt beim Oeffnen des Reiters: Hier werden PREISE
  // geschrieben. Ein falsch gepflegter Vario-Kanalpreis landete sonst ungefragt als
  // Angebotspreis bei Kaufland, ausgeloest von einem blossen Seitenaufruf.
  const ohneBedingungen = zeilenFuerVerkaufsbedingungen(rows);
  const bedingungenBlock = ohneBedingungen.length > 0 ? `
    <div class="klv-create">
      <span class="klv-create-label">Ohne Verkaufsbedingungen: ${ohneBedingungen.length}</span>
      <button type="button" class="v-button v-button--sm klv-fill-conditions"
              title="Übernimmt Preis, Menge, Zustand, Bearbeitungszeit, Versandgruppe und MwSt-Kennzeichen aus dem Mapping. Bereits gepflegte Varianten bleiben unberührt.">
        Verkaufsbedingungen ergänzen (${ohneBedingungen.length})
      </button>
    </div>` : '';

  const rowsHtml = rows.map((r) => {
    const pct = r.totalCount > 0 ? Math.round((r.filledCount / r.totalCount) * 100) : 0;
    const barCls = r.requiredGap ? 'klv-bar--gap' : 'klv-bar--ok';
    const name = r.isParent
      ? '★ Variantenvater <span class="klv-parent-note">(wird nicht gelistet)</span>'
      : escapeHtml(r.combinationLabel || r.articleId);
    const missingTitle = r.requiredMissing.length ? ` title="Fehlende Pflichtfelder: ${escapeHtml(prettyMissing(r.requiredMissing))}"` : '';
    const gapMark = r.requiredGap ? ' <span class="klv-gap-mark">(!)</span>' : '';
    // Ebenfalls nur bei geglueckter Pruefung: Sonst bekaeme eine gescheiterte Zeile den
    // "Anlegen"-Knopf, obwohl das Listing laengst existiert.
    const isMissingChild = !r.isParent && !r.listingId && r.pruefstatus !== 'fehler';

    // Ohne Mapping-Profil wurde NICHTS geprueft. Dann steht dort "nicht geprueft" statt
    // "0/0" -- eine Null saehe aus wie ein Befund, und zwar wie ein schlechter.
    // Ein gescheiterter Zeilencheck ist KEIN Befund. Bis zum 2026-08-16 kam er als
    // "0/0 (!)" heraus, ununterscheidbar von "geprüft, nichts gefunden".
    const progressCell = r.pruefstatus === 'fehler'
      ? `<td class="klv-progress"><span class="klv-check-fail" title="${escapeHtml(r.fehlerText ?? 'Unbekannter Fehler')}"><span class="v-icon fa-solid fa-circle-exclamation"></span> nicht prüfbar</span></td>`
      : ohneProfil || r.pruefstatus === 'kein-profil'
        ? `<td class="klv-progress"><span class="v-text-muted" title="Dafür wird ein Mapping-Profil gebraucht">nicht geprüft</span></td>`
        : isMissingChild
          ? `<td class="klv-progress"><span class="v-text-muted">— kein Listing</span></td>`
          : `<td class="klv-progress"${missingTitle}>${dreiBalken(r)}</td>`;

    // "Mapping" ist kein Knopf mehr, sondern ein Link nach Vario: er oeffnet die
    // Verkaufskanal-Zuordnung GENAU dieser Variante in einem neuen Fenster.
    // Ohne Basis-URL gibt es keinen sinnvollen Link — dann steht der Grund im Titel,
    // statt ins Leere zu klicken. Zur Basis-URL siehe Kommentar an varioUrlForRow.
    const mappingLink = varioBaseUrl
      ? `<a class="v-button v-button--sm" href="${escapeHtml(varioUrlForRow(varioBaseUrl, r.articleId, r.listingId))}" target="_blank" rel="noopener noreferrer">Mapping</a>`
      : `<span class="v-button v-button--sm v-button--disabled" title="Die Vario-Adresse dieses Mandanten ist nicht hinterlegt, deshalb gibt es keinen Link.">Mapping</span>`;

    const actionsCell = isMissingChild
      ? `<td class="klv-actions"><button type="button" class="v-button v-button--sm klv-create-one">Anlegen</button></td>`
      : `<td class="klv-actions">${mappingLink}</td>`;

    const varioLink = varioBaseUrl
      ? ` <a href="${escapeHtml(varioUrlForRow(varioBaseUrl, r.articleId, r.listingId))}" target="_blank" rel="noopener noreferrer" class="klv-vario-link" title="${r.listingId ? 'Listing in Vario öffnen' : 'Artikel in Vario öffnen'}"><span class="v-icon fa-regular fa-arrow-up-right-from-square"></span></a>`
      : '';

    // Die Vaterzeile traegt statt Feldfortschritt/Aktionen die Auswahl der
    // Variantenattribute. Der Vater ist kein Kaufland-Produkt — sein Feldstand sagt
    // ueber ihn nichts aus, die Merkmale der GRUPPE dagegen gehoeren genau hierher.
    if (r.isParent) {
      return `
      <tr data-article-id="${escapeHtml(r.articleId)}" data-listing-id="${escapeHtml(r.listingId ?? '')}">
        <td class="klv-name" title="Der Variantenvater ist bei Kaufland kein eigenes Produkt und wird nicht gepusht - gelistet werden die Varianten, jede mit eigener EAN.">${name}${varioLink}</td>
        <!-- colspan 3: Fortschritt, Status und Aktionen. Seit 2026-08-17 kam die
             Status-Spalte dazu; ein stehengebliebenes colspan=2 haette die Vaterzeile
             gegenueber den Kindern verschoben. -->
        <td class="klv-vattr" colspan="3">
          ${variantAttrPicker(auswaehlbar, gewaehlt, varioDimensionen, zuordnung)}
        </td>
      </tr>`;
    }

    // Fehlt ein Variantenattribut, ist das kein Schoenheitsfehler: Kaufland kann diese
    // Variante dann nicht in die Gruppe einsortieren. Deshalb direkt unter dem Namen.
    const warnung = variantAttrWarnung(r.variantAttrMissing, auswaehlbar);
    const warnZeile = warnung
      ? `<div class="klv-vattr-warn"><span class="v-icon fa-solid fa-triangle-exclamation"></span> ${escapeHtml(warnung)}</div>`
      : '';

    return `
      <tr data-article-id="${escapeHtml(r.articleId)}" data-listing-id="${escapeHtml(r.listingId ?? '')}">
        <td class="klv-name">${name}${varioLink}${warnZeile}</td>
        ${progressCell}
        <td class="klv-status" data-status-fuer="${escapeHtml(r.listingId ?? '')}">${statusZelleHtml(r)}</td>
        ${actionsCell}
      </tr>`;
  }).join('');

  host.innerHTML = `
    <div class="klv-shell">
      <div class="klv-header">
        <span>${ohneProfil
          ? `Varianten · <strong>${children.length}</strong> gefunden`
          : `Varianten · <strong>${listable} / ${children.length}</strong> listbar`}</span>
        ${onlineBadge(stand, spiegelStand)}
      </div>
      <div class="klv-listen">
        <button type="button" class="v-button v-button--primary v-button--sm klv-listen-alle"${listbareKinder.length === 0 ? ' disabled' : ''}>
          <span class="v-icon fa-regular fa-cloud-arrow-up"></span> Alle Varianten listen (${listbareKinder.length})
        </button>
        ${stand.alleOnline
          ? `<button type="button" class="v-button v-button--sm klv-feed"${gewaehlt.length === 0 ? ' disabled title="Dafür müssen am Vater Variantenattribute gewählt sein."' : ''}>
              <span class="v-icon fa-regular fa-file-arrow-up"></span> Variantendatei einreichen
            </button>`
          : `<span class="v-text-muted klv-feed-hint">Die Variantendatei lässt sich einreichen, sobald alle Varianten bei Kaufland online sind.</span>`}
      </div>
      ${ohneProfil ? `
      <div class="klv-note">
        Für diese Kategorie ist noch kein Mapping-Profil gespeichert. Die Varianten und ihre
        Verkaufskanäle siehst du trotzdem, und fehlende Listings kannst du hier anlegen.
        <strong>Feldprüfung und Bildstatus bleiben aus</strong>, bis im Reiter „Mapping“ ein
        Profil gespeichert ist.
      </div>` : ''}
      ${createBlock}
      ${bedingungenBlock}
      <table class="klv-table">
        <thead><tr><th>Artikel</th><th title="Zählt nur, worauf es ankommt: die Pflichtfelder der Kaufland-Kategorie und die am Vater gewählten Variantenattribute. Nicht alle gemappten Felder.">Pflicht + Variantenattribute</th><th title="Der interne Stand im Aufschalt-Ablauf, samt Fehler des letzten Versuchs.">Status</th><th>Aktionen</th></tr></thead>
        <tbody>${rowsHtml}</tbody>
      </table>
      <div class="klv-standzeile" hidden>
        <span class="klv-stand-text"></span>
        <button type="button" class="v-button v-button--sm klv-neu-laden">
          <span class="v-icon fa-regular fa-arrows-rotate"></span> Aktualisieren
        </button>
      </div>
    </div>`;

  wireVariantAttrs(host, ctx, listing, auswaehlbar, gewaehlt, varioDimensionen);
  wireCreate(host, ctx, listing, rows);
  wireListenUndFeed(host, ctx, listing, listbareKinder);
  beobachteStatus(host, ctx, listing, rows);
}

/**
 * Haelt die Statuszellen aktuell, ohne dass der Kunde neu laden muss.
 *
 * Anlass (2026-08-17): Der Betreiber wollte nach dem Listen sehen, wie die Fehler
 * verschwinden und die Status umspringen, "es sei denn wir bekommen das direkt ohne
 * neuladen hin".
 *
 * EINE Abfrage ueber ALLE Zeilen je Takt, nicht eine je Zeile. Und ausschliesslich auf
 * `kaufland.listings` — der Varianten-Check selbst wird NICHT wiederholt: Der macht je
 * Variante einen Vario-Rundlauf und waere alle drei Sekunden nicht vertretbar. Deshalb
 * ersetzt der Poller nur die Statuszellen im DOM.
 *
 * Direkt-SELECT statt RPC ist erlaubt: `authenticated` hat SELECT auf `kaufland.listings`,
 * und die RLS-Policy filtert auf den Mandanten. Kein Service-Role-Schluessel im Browser.
 *
 * Zeitbudget wie im Detail-Fenster (`watchListingStatus`): alle 3 s, hoechstens 2 Minuten.
 * Kauflands Freigabe dauert oft laenger — dafuer erscheint danach die Standzeile mit
 * "Aktualisieren", statt die Ansicht still veralten zu lassen.
 */
function beobachteStatus(
  host: HTMLElement,
  ctx: VariantsTabCtx,
  listing: VariantsTabListing,
  rows: readonly VariantCheckRow[],
): void {
  const standzeile = host.querySelector<HTMLElement>('.klv-standzeile');
  const standText = host.querySelector<HTMLElement>('.klv-stand-text');
  host.querySelector<HTMLButtonElement>('.klv-neu-laden')?.addEventListener('click', () => {
    void renderVariantsTab(host, ctx, listing);
  });

  // Einen etwaigen Poller aus einem frueheren Rendern abraeumen. OHNE das laeuft er weiter:
  // `host.isConnected` bleibt wahr, weil renderVariantsTab nur innerHTML ersetzt, und drei
  // Stellen loesen ein Neu-Rendern aus. Vor diesem Riegel stapelten sich die Timer.
  const registerSchluessel = listing.vario_listing_id;
  const vorheriger = timerVergessen(registerSchluessel);
  if (vorheriger !== null) clearTimeout(vorheriger);

  const ids = rows.filter((r) => !r.isParent && r.listingId).map((r) => r.listingId as string);
  const zeigeStand = (): void => {
    if (!standzeile || !standText) return;
    const jetzt = new Date().toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
    standText.textContent = `Stand von ${jetzt}`;
    standzeile.hidden = false;
  };

  if (ids.length === 0 || alleImEndzustand(rows.filter((r) => !r.isParent).map((r) => r.listingStatus))) {
    zeigeStand();
    return;
  }

  let takte = 0;
  const MAX_TAKTE = 40; // 40 * 3 s = 2 Minuten, wie watchListingStatus
  let timer: number | undefined;

  const stopp = (): void => {
    if (timer !== undefined) clearTimeout(timer);
    timerVergessen(registerSchluessel);
    zeigeStand();
  };

  // Jeden neuen Takt im Register hinterlegen; ein doppelt gestarteter Poller wuerde damit
  // sofort auffallen und der alte abgeraeumt.
  const merken = (id: number): void => {
    const alt = timerMerken(registerSchluessel, id);
    if (alt !== null && alt !== id) clearTimeout(alt);
  };

  const takt = async (): Promise<void> => {
    // Der Reiter kann inzwischen ersetzt worden sein (Tab-Wechsel, Neu-Rendern).
    if (!host.isConnected) return;
    takte += 1;
    const { data, error } = await ctx.supabase
      .schema('kaufland')
      .from('listings')
      .select('vario_listing_id, status, last_error, kaufland_status_reason')
      .in('vario_listing_id', ids);
    if (error || !data) { stopp(); return; }

    const stati: (string | undefined)[] = [];
    for (const z of data as Array<{ vario_listing_id: string; status?: string; last_error?: string | null; kaufland_status_reason?: string | null }>) {
      stati.push(z.status);
      const zelle = host.querySelector<HTMLElement>(`.klv-status[data-status-fuer="${CSS.escape(z.vario_listing_id)}"]`);
      if (!zelle) continue;
      // Die Unit kommt aus dem Kaufland-Spiegel und wird hier NICHT nachgeladen: Sie
      // aendert sich nur mit dem 2-Stunden-Abgleich, nicht durch unseren Push. Wir
      // behalten den Stand aus dem variant-check.
      const alt = rows.find((r) => r.listingId === z.vario_listing_id);
      zelle.innerHTML = statusZelleHtml({
        isParent: alt?.isParent ?? false,
        listingId: z.vario_listing_id,
        listingStatus: z.status,
        lastError: z.last_error ?? null,
        statusReason: z.kaufland_status_reason ?? null,
        unit: alt?.unit,
      });
    }

    if (alleImEndzustand(stati) || takte >= MAX_TAKTE) { stopp(); return; }
    timer = setTimeout(() => { void takt(); }, 3000) as unknown as number;
    merken(timer);
  };

  timer = setTimeout(() => { void takt(); }, 3000) as unknown as number;
  merken(timer);
}

/** Die Statuszelle EINER Zeile. Getrennt vom Zeilen-Renderer, weil der Poller sie einzeln
 *  ersetzt — ein vollstaendiges Neu-Rendern wuerde `variant-check` erneut ausloesen, und das
 *  ist der teure Weg mit einem Vario-Rundlauf je Variante. */
function statusZelleHtml(r: {
  isParent: boolean;
  listingId: string | null;
  listingStatus?: string | undefined;
  lastError?: string | null | undefined;
  statusReason?: string | null | undefined;
  unit?: { status?: string | null; stock?: number | null } | undefined;
}): string {
  if (r.isParent) return '<span class="v-text-muted">—</span>';
  if (!r.listingId) return '<span class="v-text-muted">kein Listing</span>';
  const z = variantenStatusZelle({
    status: r.listingStatus,
    lastError: r.lastError,
    statusReason: r.statusReason,
    unit: r.unit,
  });
  const hinweis = z.hinweis
    ? `<div class="klv-status-hinweis" title="${escapeHtml(z.hinweis)}">${escapeHtml(z.hinweis)}</div>`
    : '';
  return `<span class="v-pill ${z.cls}">${escapeHtml(z.label)}</span>${hinweis}`;
}

/**
 * Der Listen-Knopf des Vaters und die Einreichung der Variantendatei.
 *
 * LISTEN faechert auf die Kinder auf und nutzt fuer jedes `kaufland_enqueue_listing_publish`
 * — denselben Weg wie die Einzelaufschaltung. Keine zweite Aufschalt-Logik, die auseinander-
 * laufen koennte. Der Vater selbst wird NICHT aufgeschaltet: Bei Kaufland ist er kein
 * Produkt, der Versuch endet in einer Meldung statt in einem Angebot.
 *
 * EINREICHEN ist bewusst ein eigener Knopf und laeuft nicht automatisch nach dem Listen.
 * Zwei Gruende: Zwischen Aufschalten und "online" liegt Kauflands Pruefung, und der
 * Feed-Import bricht derzeit unerklaert ab (Supportanfrage laeuft). Ein Automatismus
 * verbrauchte dabei still eines der 30 Tages-Kontingente pro Versuch.
 */
function wireListenUndFeed(
  host: HTMLElement,
  ctx: VariantsTabCtx,
  listing: VariantsTabListing,
  listbareKinder: VariantCheckRow[],
): void {
  host.querySelector<HTMLButtonElement>('.klv-listen-alle')?.addEventListener('click', (ev) => {
    const btn = ev.currentTarget as HTMLButtonElement;
    void (async () => {
      btn.disabled = true;
      let ok = 0;
      const fehler: string[] = [];
      for (const kind of listbareKinder) {
        // Das Listing ist die Inbox-Zeile, nicht das Vario-Listing. `listingId` an der Zeile
        // ist die Vario-Seite; die Inbox-UUID holen wir ueber den Artikel.
        const { data: inbox } = await ctx.supabase
          .schema('kaufland')
          .from('listings')
          .select('id')
          .eq('vario_article_id', kind.articleId)
          .eq('storefront', listing.storefront)
          .maybeSingle();
        const inboxId = (inbox as { id?: string } | null)?.id;
        if (!inboxId) { fehler.push(`${kind.combinationLabel || kind.articleId}: kein Lister-Eintrag`); continue; }
        const { error } = await ctx.supabase.rpc('kaufland_enqueue_listing_publish', { _listing_id: inboxId });
        if (error) fehler.push(`${kind.combinationLabel || kind.articleId}: ${error.message}`);
        else ok += 1;
      }
      btn.disabled = false;
      if (fehler.length === 0) {
        showToast(`${ok} Variante(n) zum Listen eingereiht`, 'success');
      } else {
        console.warn('[variants] Listen teilweise fehlgeschlagen', fehler);
        showToast(`${ok} eingereiht, ${fehler.length} fehlgeschlagen — Einzelheiten in der Konsole`, 'error');
      }
      if (host.isConnected) await renderVariantsTab(host, ctx, listing);
    })();
  });

  const feedBtn = host.querySelector<HTMLButtonElement>('.klv-feed');
  feedBtn?.addEventListener('click', () => {
    // Zweistufiger Klick statt Browser-Dialog: window.confirm ist im Projekt verboten,
    // und eine Einreichung verbraucht eines von 30 Tages-Kontingenten.
    if (feedBtn.dataset['bestaetigt'] !== 'ja') {
      feedBtn.dataset['bestaetigt'] = 'ja';
      feedBtn.classList.add('v-button--primary');
      feedBtn.innerHTML = '<span class="v-icon fa-regular fa-circle-check"></span> Wirklich einreichen?';
      return;
    }
    void (async () => {
      feedBtn.disabled = true;
      const { error } = await ctx.supabase.rpc('kaufland_enqueue_variant_suggestions', {
        _parent_listing_id: listing.id,
      });
      feedBtn.disabled = false;
      if (error) {
        showToast(`Einreichung fehlgeschlagen: ${error.message}`, 'error');
        return;
      }
      showToast('Variantendatei eingereicht. Kaufland prüft den Import, das dauert einige Minuten.', 'success');
      feedBtn.dataset['bestaetigt'] = '';
      feedBtn.innerHTML = '<span class="v-icon fa-regular fa-file-arrow-up"></span> Variantendatei einreichen';
    })();
  });
}

function wireCreate(host: HTMLElement, ctx: VariantsTabCtx, listing: VariantsTabListing, rows: VariantCheckRow[]): void {
  function readInherit(): { description: boolean; price: boolean; optional_fields: boolean; name: boolean } {
    const out = { description: false, price: false, optional_fields: false, name: false };
    host.querySelectorAll<HTMLInputElement>('.klv-inh').forEach((cb) => {
      const key = cb.dataset['inh'];
      if (key === 'description' || key === 'price' || key === 'optional_fields' || key === 'name') {
        out[key] = cb.checked;
      }
    });
    return out;
  }

  async function enqueueCreate(childIds: string[]): Promise<void> {
    if (childIds.length === 0) return;
    const inherit = readInherit();
    showToast(`Lege ${childIds.length} Variante(n) an …`, 'info');
    // Synchron: warten bis Vario die Listings angelegt + Inbox-Rows geschrieben hat,
    // dann sofort den Tab neu rendern. Edge-Function-Timeout ~150s.
    const resp = await callFn<{
      rows: Array<{ articleId: string; status: 'created' | 'skipped' | 'failed'; reason?: string }>;
      summary: { created: string[]; skipped: string[]; failed: Array<{ articleId: string; reason: string }>; total: number };
    }>(ctx, 'kaufland-create-variant-listings', {
      parent_article_id: listing.vario_article_id,
      parent_listing_id: listing.vario_listing_id,
      storefront: listing.storefront,
      vario_category_id: listing.vario_category_id,
      kaufland_category_id: listing.kaufland_category_id,
      child_article_ids: childIds,
      inherit,
    });
    if (!resp.ok) {
      showToast(`Anlage fehlgeschlagen: ${resp.error}`, 'error');
      return;
    }
    const s = resp.value.summary;
    const parts: string[] = [];
    if (s.created.length) parts.push(`${s.created.length} angelegt`);
    if (s.skipped.length) parts.push(`${s.skipped.length} bereits vorhanden`);
    if (s.failed.length) parts.push(`${s.failed.length} fehlgeschlagen`);
    const kind = s.failed.length > 0 ? 'error' : 'success';
    showToast(parts.join(' · ') || 'Anlage durch', kind);
    if (s.failed.length > 0) {
      console.warn('[variants] some children failed', resp.value.summary.failed);
    }
    // `s.created` weiterreichen: Der naechste Durchlauf schreibt genau diesen Kindern die
    // Verkaufsbedingungen. `skipped` bleibt bewusst draussen — diese Listings gab es schon,
    // und ihre womoeglich von Hand gepflegten Preise duerfen nicht ueberschrieben werden.
    if (host.isConnected) await renderVariantsTab(host, ctx, listing, s.created);
  }

  host.querySelector<HTMLButtonElement>('.klv-create-all')?.addEventListener('click', () => {
    void enqueueCreate(rows.filter((r) => !r.isParent && !r.listingId).map((r) => r.articleId));
  });
  host.querySelectorAll<HTMLButtonElement>('.klv-create-one').forEach((btn) => {
    btn.addEventListener('click', () => {
      const tr = btn.closest('tr');
      const articleId = tr?.getAttribute('data-article-id') ?? '';
      if (articleId) void enqueueCreate([articleId]);
    });
  });

  // Verkaufsbedingungen fuer Zeilen ergaenzen, die noch keine haben. Zweistufig, weil hier
  // Preise geschrieben werden und `window.confirm` im Vario-Slot verboten ist (CLAUDE.md).
  const fillBtn = host.querySelector<HTMLButtonElement>('.klv-fill-conditions');
  if (fillBtn) {
    let bestaetigt = false;
    const beschriftung = fillBtn.textContent ?? '';
    fillBtn.addEventListener('click', () => {
      if (!bestaetigt) {
        bestaetigt = true;
        fillBtn.textContent = 'Wirklich? Nochmal klicken';
        fillBtn.classList.add('v-button--primary');
        // Zuruecksetzen, damit ein vergessener erster Klick nicht als scharfer Knopf
        // liegen bleibt.
        setTimeout(() => {
          if (!fillBtn.isConnected || !bestaetigt) return;
          bestaetigt = false;
          fillBtn.textContent = beschriftung;
          fillBtn.classList.remove('v-button--primary');
        }, 5000);
        return;
      }
      fillBtn.disabled = true;
      fillBtn.textContent = 'Übernehme …';
      void (async () => {
        await schreibeVerkaufsbedingungen(ctx, rows);
        if (host.isConnected) await renderVariantsTab(host, ctx, listing);
      })();
    });
  }
}

/**
 * Zeigt die Phasen-Zeitmessung des variant-check am Fuss der Tabelle.
 *
 * Anlass (2026-08-18): "Der Varianten Reiter braucht nun echt lange um zu laden." Statt zu
 * raten, wo die Sekunden liegen, misst die Function die Phasen selbst; hier werden sie
 * sichtbar gemacht. Der Betreiber soll sie vorlesen koennen, ohne die Entwicklerwerkzeuge
 * zu oeffnen.
 *
 * `gesamtClient` schliesst Netz und Warteschlange mit ein, die Server-Messung nicht. Weichen
 * beide stark voneinander ab, liegt die Zeit NICHT in der Function -- das ist der eigentlich
 * interessante Vergleich.
 *
 * DIAGNOSEWERKZEUG. Wenn die Ladezeit geklaert ist, kann dieser Block ersatzlos raus; er
 * traegt nichts zum Betrieb bei.
 */
function zeigeZeitmessung(
  host: HTMLElement,
  dauer: Record<string, number> | undefined,
  zeilen: number | undefined,
  gesamtClient: number,
): void {
  const shell = host.querySelector<HTMLElement>('.klv-shell');
  if (!shell) return;

  const sek = (ms: number): string => `${(ms / 1000).toFixed(1)} s`;
  const teile: string[] = [];
  if (dauer) {
    const reihenfolge = ['vorbereitung', 'kinder_aufloesen', 'zeilen_pruefen', 'online_stand', 'listing_zustand', 'snapshot_nachtrag'];
    for (const k of reihenfolge) {
      const v = dauer[k];
      if (typeof v === 'number') teile.push(`${k.replace(/_/g, ' ')}: ${sek(v)}`);
    }
  }

  const serverGesamt = dauer?.['gesamt'];
  const kopf = typeof serverGesamt === 'number'
    ? `Ladezeit ${sek(gesamtClient)} (Server ${sek(serverGesamt)}${zeilen ? `, ${zeilen} Zeilen` : ''})`
    : `Ladezeit ${sek(gesamtClient)}`;

  const el = document.createElement('div');
  el.className = 'klv-zeitmessung';
  el.innerHTML = `<strong>${escapeHtml(kopf)}</strong>${teile.length ? ` · ${escapeHtml(teile.join(' · '))}` : ''}`;
  shell.appendChild(el);
}
